import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AppLayout } from "@/components/layout/AppLayout";
import { AuthLayout } from "@/components/layout/AuthLayout";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { ProtectedRoute } from "@/components/routing/ProtectedRoute";
import { AdminRoute } from "@/components/routing/AdminRoute";
import { ROUTES } from "@/constants/routes";

import SplashScreen from "@/pages/SplashScreen";
import WelcomeScreen from "@/pages/WelcomeScreen";
import Login from "@/pages/Login";
import GoogleAuthCallback from "@/pages/GoogleAuthCallback";
import Signup from "@/pages/Signup";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Home from "@/pages/Home";
import Categories from "@/pages/Categories";
import CategoryDetail from "@/pages/CategoryDetail";
import Search from "@/pages/Search";
import ProductDetails from "@/pages/ProductDetails";
import Cart from "@/pages/Cart";
import Checkout from "@/pages/Checkout";
import Orders from "@/pages/Orders";
import OrderDetail from "@/pages/OrderDetail";
import OrderSuccess from "@/pages/OrderSuccess";
import PaymentResult from "@/pages/PaymentResult";
import Profile from "@/pages/Profile";
import AdminLogin from "@/pages/AdminLogin";
import OpsLogin from "@/pages/OpsLogin";
import StoreLogin from "@/pages/StoreLogin";
import StaffLogin from "@/pages/StaffLogin";
import AdminDashboard from "@/pages/AdminDashboard";
import AdminProducts from "@/pages/admin/AdminProducts";
import AdminProductForm from "@/pages/admin/AdminProductForm";
import AdminCategoriesPage from "@/pages/admin/AdminCategories";
import AdminOrders from "@/pages/admin/AdminOrders";
import AdminOrderDetail from "@/pages/admin/AdminOrderDetail";
import AdminCustomers from "@/pages/admin/AdminCustomers";
import AdminCoupons from "@/pages/admin/AdminCoupons";
import AdminInventory from "@/pages/admin/AdminInventory";
import AdminNotifications from "@/pages/admin/AdminNotifications";
import AdminRiders from "@/pages/admin/AdminRiders";
import AdminDispatch from "@/pages/admin/AdminDispatch";
import AdminLiveMap from "@/pages/admin/AdminLiveMap";
import AdminStores from "@/pages/admin/AdminStores";
import AdminTeam from "@/pages/admin/AdminTeam";
import AdminActivity from "@/pages/admin/AdminActivity";
import AdminSettlements from "@/pages/admin/AdminSettlements";
import AdminAttendance from "@/pages/admin/AdminAttendance";
import AdminAnalytics from "@/pages/admin/AdminAnalytics";
import AdminStoreSettings from "@/pages/admin/AdminStoreSettings";
import AdminProfile from "@/pages/admin/AdminProfile";
import AdminWallet from "@/pages/admin/AdminWallet";
import AdminUserManagement from "@/pages/admin/AdminUserManagement";
import AdminFirebase from "@/pages/admin/AdminFirebase";
import OpsSettings from "@/pages/admin/OpsSettings";
import WalletPage from "@/pages/Wallet";
import { MembershipPage, AdminMemberships } from "@/pages/Membership";
import { ReferralsPage, AdminReferrals } from "@/pages/Referrals";
import {
    AboutPage,
    CareersPage,
    PressPage,
    ContactPage,
    HelpCenterPage,
    TermsPage,
    PrivacyPage,
} from "@/pages/CompanyPages";
import PublicTrack from "@/pages/PublicTrack";
import RiderLogin from "@/pages/rider/RiderLogin";
import RiderDashboard from "@/pages/rider/RiderDashboard";
import RiderActive from "@/pages/rider/RiderActive";
import RiderEarnings from "@/pages/rider/RiderEarnings";
import RiderHistory from "@/pages/rider/RiderHistory";
import RiderLayout from "@/components/layout/RiderLayout";
import RiderRoute from "@/components/routing/RiderRoute";
import NotFound from "@/pages/NotFound";

export function AppRoutes() {
    const location = useLocation();
    // Emergent Google OAuth lands on `{origin}/home#session_id=...` — handle before normal routing.
    if (location.hash?.includes("session_id=")) {
        return <GoogleAuthCallback />;
    }
    return (
        <Routes>
            {/* Standalone screens */}
            <Route path={ROUTES.splash} element={<SplashScreen />} />
            <Route path={ROUTES.welcome} element={<WelcomeScreen />} />

            {/* Customer auth (centered, no navbar) */}
            <Route element={<AuthLayout />}>
                <Route path={ROUTES.login} element={<Login />} />
                <Route path={ROUTES.signup} element={<Signup />} />
                <Route path={ROUTES.forgotPassword} element={<ForgotPassword />} />
                <Route path={ROUTES.resetPassword} element={<ResetPassword />} />
                {/* Six role-isolated login portals */}
                <Route path={ROUTES.superAdminLogin} element={<AdminLogin />} />
                <Route path={ROUTES.adminLogin} element={<Navigate to={ROUTES.superAdminLogin} replace />} />
                <Route path={ROUTES.opsLogin} element={<OpsLogin />} />
                <Route path={ROUTES.storeLogin} element={<StoreLogin />} />
                <Route path={ROUTES.staffLogin} element={<StaffLogin />} />
                <Route path={ROUTES.riderLogin} element={<RiderLogin />} />
            </Route>

            {/* Public app shell */}
            <Route element={<AppLayout />}>
                <Route path={ROUTES.home} element={<Home />} />
                <Route path={ROUTES.categories} element={<Categories />} />
                <Route path={ROUTES.categoryDetail} element={<CategoryDetail />} />
                <Route path={ROUTES.search} element={<Search />} />
                <Route path={ROUTES.productDetails} element={<ProductDetails />} />
                <Route path={ROUTES.cart} element={<Cart />} />

                {/* Company / support info pages (public) */}
                <Route path="/about" element={<AboutPage />} />
                <Route path="/careers" element={<CareersPage />} />
                <Route path="/press" element={<PressPage />} />
                <Route path="/contact" element={<ContactPage />} />
                <Route path="/help" element={<HelpCenterPage />} />
                <Route path="/terms" element={<TermsPage />} />
                <Route path="/privacy" element={<PrivacyPage />} />

                {/* Authenticated-only pages */}
                <Route element={<ProtectedRoute />}>
                    <Route path={ROUTES.checkout} element={<Checkout />} />
                    <Route path={ROUTES.orders} element={<Orders />} />
                    <Route path={ROUTES.orderDetail} element={<OrderDetail />} />
                    <Route path={ROUTES.orderPaymentResult} element={<PaymentResult />} />
                    <Route path={ROUTES.orderSuccess} element={<OrderSuccess />} />
                    <Route path={ROUTES.profile} element={<Profile />} />
                    <Route path="/wallet" element={<WalletPage />} />
                    <Route path="/membership" element={<MembershipPage />} />
                    <Route path="/referrals" element={<ReferralsPage />} />
                </Route>
            </Route>

            {/* Role-specific dashboards — all redirect to shared /admin/* console.
                Kept as distinct routes per the strict role-isolation spec so
                URLs never bleed across roles. */}
            <Route path={ROUTES.opsDashboard} element={<Navigate to={ROUTES.adminDashboard} replace />} />
            <Route path={ROUTES.storeDashboard} element={<Navigate to={ROUTES.adminDashboard} replace />} />
            <Route path={ROUTES.staffDashboard} element={<Navigate to={ROUTES.adminDashboard} replace />} />

            {/* Admin/Ops/Store/Staff console — RBAC + store-scoping applied server-side */}
            <Route element={<AdminRoute />}>
                <Route element={<AdminLayout />}>
                    <Route path={ROUTES.adminDashboard} element={<AdminDashboard />} />
                    <Route path={ROUTES.adminProducts} element={<AdminProducts />} />
                    <Route path={ROUTES.adminProductNew} element={<AdminProductForm />} />
                    <Route path={ROUTES.adminProductEdit} element={<AdminProductForm />} />
                    <Route path={ROUTES.adminCategories} element={<AdminCategoriesPage />} />
                    <Route path={ROUTES.adminInventory} element={<AdminInventory />} />
                    <Route path={ROUTES.adminOrders} element={<AdminOrders />} />
                    <Route path={ROUTES.adminOrderDetail} element={<AdminOrderDetail />} />
                    <Route path={ROUTES.adminCustomers} element={<AdminCustomers />} />
                    <Route path={ROUTES.adminCoupons} element={<AdminCoupons />} />
                    <Route path={ROUTES.adminNotifications} element={<AdminNotifications />} />
                    <Route path={ROUTES.adminRiders} element={<AdminRiders />} />
                    <Route path={ROUTES.adminDispatch} element={<AdminDispatch />} />
                    <Route path={ROUTES.adminLiveMap} element={<AdminLiveMap />} />
                    <Route path={ROUTES.adminStores} element={<AdminStores />} />
                    <Route path={ROUTES.adminTeam} element={<AdminTeam />} />
                    <Route path={ROUTES.adminActivity} element={<AdminActivity />} />
                    <Route path={ROUTES.adminSettlements} element={<AdminSettlements />} />
                    <Route path={ROUTES.adminAttendance} element={<AdminAttendance />} />
                    <Route path="/admin/analytics" element={<AdminAnalytics />} />
                    <Route path="/admin/store-settings" element={<AdminStoreSettings />} />
                    <Route path="/admin/profile" element={<AdminProfile />} />
                    <Route path="/admin/wallet" element={<AdminWallet />} />
                    <Route path="/admin/users" element={<AdminUserManagement />} />
                    <Route path="/admin/memberships" element={<AdminMemberships />} />
                    <Route path="/admin/referrals" element={<AdminReferrals />} />
                    <Route path="/admin/firebase" element={<AdminFirebase />} />
                    <Route path="/ops/settings" element={<OpsSettings />} />
                    <Route path="/admin/settings" element={<OpsSettings />} />
                </Route>
            </Route>

            {/* Public tracking share links (no auth) */}
            <Route path={ROUTES.publicTrack} element={<PublicTrack />} />

            {/* Rider app (mobile-first shell) */}
            <Route element={<RiderRoute />}>
                <Route element={<RiderLayout />}>
                    <Route path={ROUTES.riderDashboard} element={<RiderDashboard />} />
                    <Route path={ROUTES.riderActive} element={<RiderActive />} />
                    <Route path={ROUTES.riderEarnings} element={<RiderEarnings />} />
                    <Route path={ROUTES.riderHistory} element={<RiderHistory />} />
                </Route>
            </Route>

            {/* Redirects & fallback */}
            <Route path="/index.html" element={<Navigate to="/" replace />} />
            <Route path={ROUTES.notFound} element={<NotFound />} />
        </Routes>
    );
}
